import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ phase: "checking", message: "Confirming your payment…" });
  const [tier, setTier] = useState("");
  const [cadence, setCadence] = useState("");

  useEffect(() => {
    if (!sessionId || !API_BASE) {
      setStatus({ phase: "error", message: "Missing checkout session." });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API_BASE}/api/billing/status/${encodeURIComponent(sessionId)}`, { credentials: "include" });
        if (!res.ok) throw new Error("status check failed");
        const j = await res.json();
        if (cancelled) return;
        if (j.paymentStatus === "paid") {
          setTier(j.tier || ""); setCadence(j.cadence || "");
          setStatus({ phase: "success", message: "Payment confirmed. Your plan is now active." });
          return;
        }
        if (j.status === "expired") {
          setStatus({ phase: "error", message: "This checkout session expired. Please try again." });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setStatus({ phase: "pending", message: "Payment is still processing. We'll send a confirmation email when it completes." });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (_) {
        if (cancelled) return;
        if (attempts >= MAX_POLLS) {
          setStatus({ phase: "error", message: "Could not confirm payment status. Please contact support if your card was charged." });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="flex flex-col gap-5" data-testid="checkout-success-page">
      <section>
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">RigSentinel · Billing</div>
        <h2 className="font-heading font-black text-3xl tracking-tight mt-1" data-testid="checkout-success-title">
          {status.phase === "success" ? "Welcome aboard" : status.phase === "error" ? "Payment issue" : "Confirming payment"}
        </h2>
      </section>
      <section className="card-rs p-4" data-testid="checkout-status-card" style={{ borderColor: status.phase === "success" ? "rgba(34,197,94,0.55)" : status.phase === "error" ? "rgba(239,68,68,0.6)" : "rgba(255,179,0,0.55)" }}>
        <div className="flex items-center gap-2">
          {status.phase === "checking" || status.phase === "pending" ? (
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--rs-amber)" }} />
          ) : status.phase === "success" ? (
            <CheckCircle2 className="w-4 h-4" style={{ color: "var(--rs-success)" }} />
          ) : (
            <AlertTriangle className="w-4 h-4" style={{ color: "rgb(252,165,165)" }} />
          )}
          <div className="text-sm text-[var(--rs-text-2)]" data-testid="checkout-status-message">{status.message}</div>
        </div>
        {status.phase === "success" && (
          <p className="mt-3 text-[12px] text-[var(--rs-text-3)] leading-snug">
            Plan: <span className="text-[var(--rs-text-1)]">{tier}</span> · <span>{cadence}</span>
          </p>
        )}
        <div className="mt-4 flex gap-2 flex-wrap">
          <button data-testid="checkout-go-home-btn" onClick={() => navigate("/")} className="btn-amber h-9 px-3 rounded-md label-up text-xs">Continue to RigSentinel</button>
          <button data-testid="checkout-view-plans-btn" onClick={() => navigate("/pricing")} className="btn-ghost h-9 px-3 rounded-md label-up text-xs">View pricing</button>
        </div>
      </section>
    </div>
  );
}
